import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { logConsentToDb } from "@/lib/consentLog";

const STORAGE_KEY = "dsos_cookie_consent_v1";
const VERSION = "1";

export interface CookieConsentState {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  version: string;
  updatedAt: string;
}

export type CookieConsent = CookieConsentState | null;

function loadConsent(): CookieConsent {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CookieConsentState;
    if (parsed.version !== VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveConsent(state: CookieConsentState): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

interface CookieConsentContextValue {
  consent: CookieConsent;
  getConsent: () => CookieConsentState | null;
  setConsent: (partial: Partial<Pick<CookieConsentState, "analytics" | "marketing">>) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  canUseAnalytics: () => boolean;
  canUseMarketing: () => boolean;
  openPreferences: () => void;
  closePreferences: () => void;
  setPreferencesOpen: (open: boolean) => void;
  preferencesOpen: boolean;
  bannerDismissed: boolean;
  setBannerDismissed: (dismissed: boolean) => void;
  showBanner: boolean;
  setShowBanner: (show: boolean) => void;
}

const defaultState: CookieConsentState = {
  essential: true,
  analytics: false,
  marketing: false,
  version: VERSION,
  updatedAt: new Date().toISOString(),
};

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsentState] = useState<CookieConsent>(() => loadConsent());
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [showBanner, setShowBanner] = useState(true);

  useEffect(() => {
    setConsentState(loadConsent());
  }, []);

  const getConsent = useCallback((): CookieConsentState | null => {
    return loadConsent();
  }, []);

  const setConsent = useCallback(
    (partial: Partial<Pick<CookieConsentState, "analytics" | "marketing">>) => {
      const next: CookieConsentState = {
        ...defaultState,
        ...loadConsent(),
        ...partial,
        essential: true,
        version: VERSION,
        updatedAt: new Date().toISOString(),
      };
      saveConsent(next);
      setConsentState(next);
    },
    []
  );

  const acceptAll = useCallback(() => {
    const next: CookieConsentState = {
      essential: true,
      analytics: true,
      marketing: true,
      version: VERSION,
      updatedAt: new Date().toISOString(),
    };
    saveConsent(next);
    setConsentState(next);
    setBannerDismissed(true);
    setShowBanner(false);
    logConsentToDb(next);
  }, []);

  const rejectAll = useCallback(() => {
    const next: CookieConsentState = {
      ...defaultState,
      updatedAt: new Date().toISOString(),
    };
    saveConsent(next);
    setConsentState(next);
    setBannerDismissed(true);
    setShowBanner(false);
    logConsentToDb(next);
  }, []);

  const canUseAnalytics = useCallback((): boolean => {
    const c = loadConsent();
    return c?.analytics === true;
  }, [consent]);

  const canUseMarketing = useCallback((): boolean => {
    const c = loadConsent();
    return c?.marketing === true;
  }, [consent]);

  const openPreferences = useCallback(() => {
    setPreferencesOpen(true);
  }, []);

  const closePreferences = useCallback(() => {
    setPreferencesOpen(false);
  }, []);

  const value = useMemo<CookieConsentContextValue>(
    () => ({
      consent,
      getConsent,
      setConsent,
      acceptAll,
      rejectAll,
      canUseAnalytics,
      canUseMarketing,
      openPreferences,
      closePreferences,
      setPreferencesOpen,
      preferencesOpen,
      bannerDismissed,
      setBannerDismissed,
      showBanner,
      setShowBanner,
    }),
    [
      consent,
      getConsent,
      setConsent,
      acceptAll,
      rejectAll,
      canUseAnalytics,
      canUseMarketing,
      openPreferences,
      closePreferences,
      preferencesOpen,
      bannerDismissed,
      showBanner,
    ]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentContextValue {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    return {
      consent: null,
      getConsent: () => null,
      setConsent: () => {},
      acceptAll: () => {},
      rejectAll: () => {},
      canUseAnalytics: () => false,
      canUseMarketing: () => false,
      openPreferences: () => {},
      closePreferences: () => {},
      setPreferencesOpen: () => {},
      preferencesOpen: false,
      bannerDismissed: true,
      setBannerDismissed: () => {},
      showBanner: false,
      setShowBanner: () => {},
    };
  }
  return ctx;
}
